import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Users, TrendingUp, FileText, MessageSquare, Trash2, Eye, Plus, X, RefreshCw, Fingerprint, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('admin_token', res.data.token);
      onLogin(res.data.token);
      toast.success('Logged in successfully');
    } catch {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const optionsRes = await axios.post(`${API}/admin/passkey/login-options`);
      const authResp = await startAuthentication({ optionsJSON: optionsRes.data });
      const verifyRes = await axios.post(`${API}/admin/passkey/login-verify`, authResp);
      localStorage.setItem('admin_token', verifyRes.data.token);
      onLogin(verifyRes.data.token);
      toast.success('Logged in with passkey');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Passkey login failed';
      if (!msg.includes('The operation either timed out or was not allowed')) {
        toast.error(msg);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-surface px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-lg p-10 w-full max-w-sm"
      >
        <h1 className="text-2xl font-heading font-bold mb-2">Admin Login</h1>
        <p className="text-slate-500 text-sm mb-8">Artificial Mind CRM</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text" value={username} onChange={e => setUsername(e.target.value)}
            data-testid="admin-username" className="input-dark w-full h-12 px-4 text-sm" placeholder="Username" required
          />
          <input
            type="password" value={password} onChange={e => setPassword(e.target.value)}
            data-testid="admin-password" className="input-dark w-full h-12 px-4 text-sm" placeholder="Password" required
          />
          <button
            type="submit" disabled={loading} data-testid="admin-login-btn"
            className="w-full bg-white text-black font-bold text-sm uppercase tracking-wider py-3 hover:bg-slate-200 transition-colors disabled:opacity-50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-slate-600 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <button
          onClick={handlePasskeyLogin}
          disabled={passkeyLoading}
          data-testid="admin-passkey-login-btn"
          className="w-full border border-white/20 text-white font-bold text-sm uppercase tracking-wider py-3 hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Fingerprint size={16} />
          {passkeyLoading ? 'Authenticating...' : 'Login with Passkey'}
        </button>
      </motion.div>
    </div>
  );
}

function StatusBadge({ status, onChange }) {
  const colors = {
    new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    contacted: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    qualified: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    closed: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };
  return (
    <select
      value={status}
      onChange={e => onChange(e.target.value)}
      className={`text-xs font-bold uppercase px-2 py-1 border rounded cursor-pointer bg-transparent ${colors[status] || colors.new}`}
    >
      <option value="new">New</option>
      <option value="contacted">Contacted</option>
      <option value="qualified">Qualified</option>
      <option value="closed">Closed</option>
    </select>
  );
}

function DashboardStats({ stats }) {
  const cards = [
    { label: 'Contact Inquiries', value: stats.contacts?.total || 0, sub: `${stats.contacts?.new || 0} new`, icon: MessageSquare, color: 'text-blue-400' },
    { label: 'Investor Inquiries', value: stats.investors?.total || 0, sub: `${stats.investors?.new || 0} new`, icon: TrendingUp, color: 'text-emerald-400' },
    { label: 'Case Studies', value: stats.case_studies || 0, sub: 'published', icon: FileText, color: 'text-amber-400' },
    { label: 'Testimonials', value: stats.testimonials || 0, sub: 'active', icon: Users, color: 'text-violet-400' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {cards.map(c => (
        <div key={c.label} className="glass-card rounded-lg p-6">
          <c.icon className={`w-5 h-5 ${c.color} mb-3`} />
          <p className="text-3xl font-heading font-black text-white">{c.value}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{c.label}</p>
          <p className="text-xs text-slate-600 mt-1">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

function ContactsTab({ token }) {
  const [contacts, setContacts] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(res.data);
    } catch { toast.error('Failed to load contacts'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/contacts/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    await axios.delete(`${API}/admin/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  return (
    <div data-testid="admin-contacts-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-heading font-bold">Contact Submissions</h2>
        <button onClick={load} className="text-slate-400 hover:text-white"><RefreshCw size={16} /></button>
      </div>
      {contacts.length === 0 ? (
        <p className="text-slate-500 text-sm">No contact submissions yet.</p>
      ) : (
        <div className="space-y-3">
          {contacts.map(c => (
            <div key={c.id} className="glass-card rounded-lg p-5">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <p className="text-sm font-semibold text-white">{c.name}</p>
                    <StatusBadge status={c.status || 'new'} onChange={s => updateStatus(c.id, s)} />
                  </div>
                  <p className="text-xs text-slate-500">{c.email} {c.company && `| ${c.company}`} {c.industry && `| ${c.industry}`}</p>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{c.message}</p>
                  <p className="text-xs text-slate-600 mt-2">{new Date(c.timestamp).toLocaleString()}</p>
                </div>
                <button onClick={() => remove(c.id)} className="text-slate-600 hover:text-red-400 flex-shrink-0"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvestorTab({ token }) {
  const [inquiries, setInquiries] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/investor-inquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(res.data);
    } catch { toast.error('Failed to load investor inquiries'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/investor-inquiries/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    await axios.delete(`${API}/admin/investor-inquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  return (
    <div data-testid="admin-investors-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-heading font-bold">Investor Inquiries</h2>
        <button onClick={load} className="text-slate-400 hover:text-white"><RefreshCw size={16} /></button>
      </div>
      {inquiries.length === 0 ? (
        <p className="text-slate-500 text-sm">No investor inquiries yet.</p>
      ) : (
        <div className="space-y-3">
          {inquiries.map(c => (
            <div key={c.id} className="glass-card rounded-lg p-5">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <p className="text-sm font-semibold text-white">{c.name}</p>
                    <StatusBadge status={c.status || 'new'} onChange={s => updateStatus(c.id, s)} />
                  </div>
                  <p className="text-xs text-slate-500">{c.email} {c.company && `| ${c.company}`} {c.investment_type && `| ${c.investment_type}`}</p>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{c.message}</p>
                  <p className="text-xs text-slate-600 mt-2">{new Date(c.timestamp).toLocaleString()}</p>
                </div>
                <button onClick={() => remove(c.id)} className="text-slate-600 hover:text-red-400 flex-shrink-0"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CaseStudiesTab({ token }) {
  const [studies, setStudies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', industry: '', description: '', challenge: '', solution: '', results: '', image: '', tags: '' });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/case-studies`, { headers: { Authorization: `Bearer ${token}` } });
      setStudies(res.data);
    } catch { toast.error('Failed to load case studies'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    await axios.post(`${API}/admin/seed-case-studies`, {}, { headers: { Authorization: `Bearer ${token}` } });
    load();
    toast.success('Case studies seeded to database');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    const payload = {
      ...form,
      results: form.results.split('\n').filter(Boolean),
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/case-studies/${editing}`, payload, authHeaders);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/case-studies`, payload, authHeaders);
        toast.success('Created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', industry: '', description: '', challenge: '', solution: '', results: '', image: '', tags: '' });
      load();
    } catch { toast.error('Save failed'); }
  };

  const startEdit = (cs) => {
    setForm({
      ...cs,
      results: (cs.results || []).join('\n'),
      tags: (cs.tags || []).join(', '),
    });
    setEditing(cs.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    await axios.delete(`${API}/admin/case-studies/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  return (
    <div data-testid="admin-casestudies-tab">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="text-xl font-heading font-bold">Case Studies</h2>
        <div className="flex gap-2">
          <button onClick={seed} className="text-xs text-slate-400 hover:text-white border border-white/10 px-3 py-1.5 uppercase tracking-wider">Seed to DB</button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', industry: '', description: '', challenge: '', solution: '', results: '', image: '', tags: '' }); }}
            className="text-xs text-black bg-white px-3 py-1.5 uppercase tracking-wider font-bold flex items-center gap-1"><Plus size={12} /> Add New</button>
        </div>
      </div>

      {showForm && (
        <div className="glass-card rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-bold uppercase tracking-wider">{editing ? 'Edit' : 'New'} Case Study</h3>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="input-dark h-10 px-3 text-sm" placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
              <input className="input-dark h-10 px-3 text-sm" placeholder="Industry" value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} required />
            </div>
            <input className="input-dark w-full h-10 px-3 text-sm" placeholder="Image URL" value={form.image} onChange={e => setForm({...form, image: e.target.value})} />
            <textarea className="input-dark w-full px-3 py-2 text-sm min-h-[60px]" placeholder="Short description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required />
            <textarea className="input-dark w-full px-3 py-2 text-sm min-h-[60px]" placeholder="Challenge" value={form.challenge} onChange={e => setForm({...form, challenge: e.target.value})} />
            <textarea className="input-dark w-full px-3 py-2 text-sm min-h-[60px]" placeholder="Solution" value={form.solution} onChange={e => setForm({...form, solution: e.target.value})} />
            <textarea className="input-dark w-full px-3 py-2 text-sm min-h-[60px]" placeholder="Results (one per line)" value={form.results} onChange={e => setForm({...form, results: e.target.value})} />
            <input className="input-dark w-full h-10 px-3 text-sm" placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} />
            <button type="submit" className="bg-white text-black font-bold text-xs uppercase tracking-wider px-6 py-2.5">{editing ? 'Update' : 'Create'}</button>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {studies.map(cs => (
          <div key={cs.id} className="glass-card rounded-lg p-5 flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{cs.title}</p>
              <p className="text-xs text-brand-accent mt-1">{cs.industry}</p>
              <p className="text-xs text-slate-500 mt-1 truncate">{cs.description}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => startEdit(cs)} className="text-slate-500 hover:text-white"><Eye size={14} /></button>
              <button onClick={() => remove(cs.id)} className="text-slate-600 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsTab({ token }) {
  const [testimonials, setTestimonials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', role: '', company: '', quote: '' });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/testimonials`, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(res.data);
    } catch { toast.error('Failed to load testimonials'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    await axios.post(`${API}/admin/seed-testimonials`, {}, { headers: { Authorization: `Bearer ${token}` } });
    load();
    toast.success('Testimonials seeded to database');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      if (editing) {
        await axios.put(`${API}/admin/testimonials/${editing}`, form, authHeaders);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/testimonials`, form, authHeaders);
        toast.success('Created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', role: '', company: '', quote: '' });
      load();
    } catch { toast.error('Save failed'); }
  };

  const startEdit = (t) => {
    setForm({ name: t.name, role: t.role, company: t.company, quote: t.quote });
    setEditing(t.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await axios.delete(`${API}/admin/testimonials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  return (
    <div data-testid="admin-testimonials-tab">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="text-xl font-heading font-bold">Testimonials</h2>
        <div className="flex gap-2">
          <button onClick={seed} className="text-xs text-slate-400 hover:text-white border border-white/10 px-3 py-1.5 uppercase tracking-wider">Seed to DB</button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', role: '', company: '', quote: '' }); }}
            className="text-xs text-black bg-white px-3 py-1.5 uppercase tracking-wider font-bold flex items-center gap-1"><Plus size={12} /> Add New</button>
        </div>
      </div>

      {showForm && (
        <div className="glass-card rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-bold uppercase tracking-wider">{editing ? 'Edit' : 'New'} Testimonial</h3>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input className="input-dark h-10 px-3 text-sm" placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              <input className="input-dark h-10 px-3 text-sm" placeholder="Role" value={form.role} onChange={e => setForm({...form, role: e.target.value})} required />
              <input className="input-dark h-10 px-3 text-sm" placeholder="Company" value={form.company} onChange={e => setForm({...form, company: e.target.value})} required />
            </div>
            <textarea className="input-dark w-full px-3 py-2 text-sm min-h-[80px]" placeholder="Quote" value={form.quote} onChange={e => setForm({...form, quote: e.target.value})} required />
            <button type="submit" className="bg-white text-black font-bold text-xs uppercase tracking-wider px-6 py-2.5">{editing ? 'Update' : 'Create'}</button>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {testimonials.map(t => (
          <div key={t.id} className="glass-card rounded-lg p-5 flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{t.name}</p>
              <p className="text-xs text-slate-500">{t.role}, {t.company}</p>
              <p className="text-xs text-slate-400 mt-2 italic truncate">&ldquo;{t.quote}&rdquo;</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => startEdit(t)} className="text-slate-500 hover:text-white"><Eye size={14} /></button>
              <button onClick={() => remove(t.id)} className="text-slate-600 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: Eye },
  { id: 'contacts', label: 'Contacts', icon: MessageSquare },
  { id: 'investors', label: 'Investors', icon: TrendingUp },
  { id: 'case-studies', label: 'Case Studies', icon: FileText },
  { id: 'testimonials', label: 'Testimonials', icon: Users },
  { id: 'passkeys', label: 'Passkeys', icon: KeyRound },
];

function PasskeysTab({ token }) {
  const [passkeys, setPasskeys] = useState([]);
  const [registering, setRegistering] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/passkeys`, { headers: { Authorization: `Bearer ${token}` } });
      setPasskeys(res.data);
    } catch { toast.error('Failed to load passkeys'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const registerPasskey = async () => {
    setRegistering(true);
    try {
      const optionsRes = await axios.post(
        `${API}/admin/passkey/register-options`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const regResp = await startRegistration({ optionsJSON: optionsRes.data });
      await axios.post(`${API}/admin/passkey/register-verify`, regResp, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Passkey registered successfully');
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Registration failed';
      if (!msg.includes('The operation either timed out or was not allowed')) {
        toast.error(msg);
      }
    } finally {
      setRegistering(false);
    }
  };

  const remove = async (credId) => {
    if (!window.confirm('Delete this passkey?')) return;
    await axios.delete(`${API}/admin/passkeys/${encodeURIComponent(credId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    toast.success('Passkey deleted');
    load();
  };

  return (
    <div data-testid="admin-passkeys-tab">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold">Passkey Management</h2>
          <p className="text-xs text-slate-500 mt-1">Register hardware keys or biometric passkeys for passwordless login</p>
        </div>
        <button
          onClick={registerPasskey}
          disabled={registering}
          data-testid="register-passkey-btn"
          className="text-xs text-black bg-white px-4 py-2 uppercase tracking-wider font-bold flex items-center gap-2 hover:bg-slate-200 transition-colors disabled:opacity-50"
        >
          <Fingerprint size={14} />
          {registering ? 'Registering...' : 'Register New Passkey'}
        </button>
      </div>

      {passkeys.length === 0 ? (
        <div className="glass-card rounded-lg p-8 text-center">
          <KeyRound className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-sm mb-2">No passkeys registered yet</p>
          <p className="text-slate-600 text-xs">Register a passkey to enable passwordless admin login</p>
        </div>
      ) : (
        <div className="space-y-3">
          {passkeys.map((pk) => (
            <div key={pk.credential_id} className="glass-card rounded-lg p-5 flex justify-between items-center gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <Fingerprint className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">Passkey</p>
                  <p className="text-xs text-slate-500 truncate">
                    ID: {pk.credential_id.substring(0, 20)}...
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Registered: {new Date(pk.created_at).toLocaleDateString()} &middot; Uses: {pk.sign_count}
                  </p>
                </div>
              </div>
              <button
                onClick={() => remove(pk.credential_id)}
                data-testid="delete-passkey-btn"
                className="text-slate-600 hover:text-red-400 flex-shrink-0"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStats(r.data))
      .catch(() => { localStorage.removeItem('admin_token'); setToken(null); });
  }, [token, tab]);

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  if (!token) return <LoginForm onLogin={setToken} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-surface pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-heading font-bold">Admin CRM</h1>
            <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Artificial Mind</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="text-slate-400 hover:text-white flex items-center gap-2 text-sm">
            <LogOut size={14} /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 overflow-x-auto pb-2">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider font-bold whitespace-nowrap border transition-colors ${
                tab === t.id ? 'border-white text-white bg-white/5' : 'border-white/10 text-slate-500 hover:text-white'
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'dashboard' && <DashboardStats stats={stats} />}
        {tab === 'contacts' && <ContactsTab token={token} />}
        {tab === 'investors' && <InvestorTab token={token} />}
        {tab === 'case-studies' && <CaseStudiesTab token={token} />}
        {tab === 'testimonials' && <TestimonialsTab token={token} />}
        {tab === 'passkeys' && <PasskeysTab token={token} />}
      </div>
    </div>
  );
}
